import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { googleDriveService } from '@ideoz/core/api/services/googleDrive.service';

/**
 * OAuth2 Callback Page
 * Handles the redirect from Google OAuth after user grants permissions.
 * Exchanges the authorization code for tokens via the backend,
 * then passes the access token back to the opener window via postMessage.
 */
export const OAuth2Callback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the authorization code from URL parameters
        const code = searchParams.get('code');
        const error = searchParams.get('error');

        // Handle user denial or errors from Google
        if (error) {
          console.error('OAuth error:', error);
          setStatus('error');
          setErrorMessage(error === 'access_denied'
            ? 'You denied access to Google Drive. Please try again and grant permissions.'
            : `OAuth error: ${error}`);

          // Notify opener that auth failed
          if (window.opener) {
            window.opener.postMessage(
              { type: 'GDRIVE_AUTH_ERROR', error },
              window.location.origin,
            );
          }

          setTimeout(() => window.close(), 3000);
          return;
        }

        // Validate that we have the code
        if (!code) {
          console.error('No authorization code received');
          setStatus('error');
          setErrorMessage('No authorization code received from Google');
          setTimeout(() => window.close(), 3000);
          return;
        }

        // Exchange the code for tokens via the backend
        // The backend stores the refresh_token and returns the access_token
        const redirectUrl = window.location.origin + window.location.pathname;
        const result = await googleDriveService.completeOAuthCallback(code, redirectUrl);

        setStatus('success');

        // Send the access token back to the opener window (UploadFilesDialog)
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'GDRIVE_AUTH_SUCCESS',
              accessToken: (result as any).accessToken,
              expiresAt: (result as any).expiresAt,
            },
            window.location.origin,
          );

          setTimeout(() => window.close(), 500);
        } else {
          // If not a popup (e.g. same-tab redirect), store and navigate back
          if ((result as any).accessToken) {
            sessionStorage.setItem('gdrive_access_token', (result as any).accessToken);
            sessionStorage.setItem('gdrive_expires_at', String((result as any).expiresAt));
          }
          navigate(-1);
        }
      } catch (error: any) {
        console.error('Error during OAuth callback:', error);
        setStatus('error');
        setErrorMessage(error.message || 'Failed to complete authentication');

        if (window.opener) {
          window.opener.postMessage(
            { type: 'GDRIVE_AUTH_ERROR', error: error.message },
            window.location.origin,
          );
        }

        setTimeout(() => window.close(), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Connecting to Google Drive...
            </h2>
            <p className="text-gray-600">
              Please wait while we complete the authentication.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Successfully Connected!
            </h2>
            {errorMessage ? (
              // Localhost: Show the authorization code
              <>
                <p className="text-gray-600 mb-4">
                  Authorization successful. Here is your code:
                </p>
                <div className="bg-gray-100 rounded-lg p-4 mb-4">
                  <p className="text-sm font-mono text-gray-800 break-all select-all">
                    {errorMessage}
                  </p>
                </div>
                <p className="text-sm text-gray-500">
                  Window will close automatically in 3 seconds...
                </p>
              </>
            ) : (
              // Production: Simple redirect message
              <p className="text-gray-600">
                Redirecting you back to Ideoz...
              </p>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Connection Failed
            </h2>
            <p className="text-gray-600 mb-4">
              {errorMessage}
            </p>
            <p className="text-sm text-gray-500">
              Redirecting back to Ideoz...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuth2Callback;
